import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineOfficeBuilding,
  HiOutlineRefresh,
  HiOutlineTrendingDown,
  HiOutlineDocumentText,
  HiOutlineX,
  HiOutlineExclamation,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const REASON_LABELS = {
  pricing_too_high: 'Pricing too high',
  budget_constraints: 'Budget constraints',
  switched_competitor: 'Switched competitor',
  product_gap: 'Product gap',
  business_closed: 'Business closed',
  no_response: 'No response',
  other: 'Other',
};

const REASON_COLORS = {
  pricing_too_high: 'bg-orange-100 text-orange-700',
  budget_constraints: 'bg-amber-100 text-amber-700',
  switched_competitor: 'bg-purple-100 text-purple-700',
  product_gap: 'bg-blue-100 text-blue-700',
  business_closed: 'bg-gray-100 text-gray-600',
  no_response: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-600',
};

const RenewalLossReport = () => {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(90);
  const [recordTarget, setRecordTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['renewal-loss-report', days],
    queryFn: () => superAdminAPI.getRenewalLossReport({ days }),
    refetchInterval: 120000,
  });

  const recordMutation = useMutation({
    mutationFn: superAdminAPI.recordRenewalLossReason,
    onSuccess: () => {
      toast.success('Loss reason recorded');
      setRecordTarget(null);
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['renewal-loss-report'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record reason'),
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const byReason = body.byReason || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, plan: '', reason: '', overdue: '' });
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.organizationName, r.organizationEmail, r.organizationPhone, r.planName, r.lossNotes],
  }).filter((r) =>
    (!filters.plan || r.planName === filters.plan)
    && (!filters.reason || (filters.reason === 'not_recorded' ? !r.lossReason : r.lossReason === filters.reason))
    && (!filters.overdue
      || (filters.overdue === 'lt7' && r.overdueDays < 7)
      || (filters.overdue === '7to30' && r.overdueDays >= 7 && r.overdueDays < 30)
      || (filters.overdue === 'gte30' && r.overdueDays >= 30)));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineTrendingDown className="w-7 h-7 text-red-500" />
            Lost Renewals
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Customers whose subscription ended without renewing, and the reason
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="renewal-loss"
        summary="Find out why customers leave, so the team can win them back or fix the cause."
        points={[
          ["Total lost", "Subscriptions that ended without renewal in the period."],
          ["Reasons recorded", "How many of those have a reason filled in."],
          ["Overdue", "Days since the subscription ended."],
          ["Loss reason", "Why the customer did not renew. Add one with the action button."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Lost</p>
          <p className="text-3xl font-bold text-red-500 mt-2">{stats.totalLost ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Expired without renewal (last {body.windowDays ?? days} days)</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reasons Recorded</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.reasonsRecorded ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">{stats.recordingRate ?? 0}% of lost renewals</p>
        </div>
        {/* Reason breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Loss Reasons</p>
          {Object.keys(byReason).length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No reasons recorded yet</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(byReason).map(([key, count]) => (
                <span
                  key={key}
                  className={`px-2 py-1 rounded-full text-xs font-medium ${REASON_COLORS[key] || 'bg-gray-100 text-gray-600'}`}
                >
                  {REASON_LABELS[key] || key}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lost subscriptions table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Expired Without Renewal</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            showPriority={false}
            searchPlaceholder="Search organization, email, phone, notes…"
            extraActive={!!(filters.plan || filters.reason || filters.overdue)}
            onResetExtra={() => setFilters((f) => ({ ...f, plan: '', reason: '', overdue: '' }))}
            exportProps={{
              fileName: `renewal-loss-${days}d`,
              getRows: () => rows,
              columns: [
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Email', value: (r) => r.organizationEmail },
                { header: 'Phone', value: (r) => r.organizationPhone },
                { header: 'Plan', value: (r) => r.planName },
                { header: 'Expired On', value: (r) => formatExportDate(r.dueDate, false) },
                { header: 'Overdue (days)', value: (r) => r.overdueDays },
                { header: 'Loss Reason', value: (r) => (r.lossReason ? REASON_LABELS[r.lossReason] || r.lossReason : 'Not recorded') },
                { header: 'Notes', value: (r) => r.lossNotes },
              ],
            }}
          >
            <FilterSelect value={filters.plan} onChange={(v) => setFilters((f) => ({ ...f, plan: v }))}
              allLabel="All Plans" options={uniqueSorted(allRows.map((r) => r.planName)).map((p) => [p, p])} />
            <FilterSelect value={filters.reason} onChange={(v) => setFilters((f) => ({ ...f, reason: v }))}
              allLabel="All Reasons" options={[['not_recorded', 'Not recorded'], ...Object.entries(REASON_LABELS)]} />
            <FilterSelect value={filters.overdue} onChange={(v) => setFilters((f) => ({ ...f, overdue: v }))}
              allLabel="Any Overdue" options={[['lt7', 'Under 7 days'], ['7to30', '7 – 29 days'], ['gte30', '30+ days']]} />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading loss report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0
              ? 'No lost renewals match these filters'
              : 'No lost renewals in this period — every expiring subscription renewed 🎉'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Expired On</th>
                  <th className="px-4 py-3">Overdue</th>
                  <th className="px-4 py-3">Loss Reason</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={`${row.organizationId}-${row.dueDate}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{row.organizationName}</p>
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">{row.organizationEmail || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {row.organizationPhone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.planName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(row.dueDate)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        row.overdueDays >= 30 ? 'bg-red-100 text-red-700'
                        : row.overdueDays >= 7 ? 'bg-orange-100 text-orange-700'
                        : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {row.overdueDays}d
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.lossReason ? (
                        <div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${REASON_COLORS[row.lossReason] || 'bg-gray-100 text-gray-600'}`}>
                            {REASON_LABELS[row.lossReason] || row.lossReason}
                          </span>
                          {row.lossNotes && (
                            <p className="text-xs text-gray-400 mt-1 max-w-[220px] truncate" title={row.lossNotes}>
                              {row.lossNotes}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <HiOutlineExclamation className="w-3.5 h-3.5 text-amber-400" />
                          Not recorded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setRecordTarget(row);
                            setReason(row.lossReason || '');
                            setNotes(row.lossNotes || '');
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary-200
                                     text-primary-600 text-xs font-medium hover:bg-primary-50"
                        >
                          <HiOutlineDocumentText className="w-3.5 h-3.5" />
                          {row.lossReason ? 'Update Reason' : 'Record Reason'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record-reason modal */}
      {recordTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Record Loss Reason</h3>
              <button onClick={() => setRecordTarget(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{recordTarget.organizationName}</span>
                {' '}— subscription expired {formatDate(recordTarget.dueDate)}
                {` (${recordTarget.overdueDays} day${recordTarget.overdueDays !== 1 ? 's' : ''} overdue)`}
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Reason
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                >
                  <option value="" disabled>Select a reason…</option>
                  {Object.entries(REASON_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any additional context…"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setRecordTarget(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  recordMutation.mutate({
                    organizationId: recordTarget.organizationId,
                    dueDate: recordTarget.dueDate,
                    reason,
                    notes,
                  })
                }
                disabled={!reason || recordMutation.isLoading}
                className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {recordMutation.isLoading ? 'Saving…' : 'Save Reason'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewalLossReport;